import { useTkv2HandleIconClick } from '@aloft/toolkit-frontend/src/components/ReferenceBar/useTkv2HandleIconClick.ts';
import { Tkv2ReferenceBarProperty as Tkv2ReferenceBarPropertyType } from '@aloft/toolkit-frontend/src/controller/types.ts';
import { Flex, Icon, IconButton, Tooltip, Typography } from '@aloft/toolkit-sui';

import { useLocation } from 'react-router';

import { useActiveReferenceBarID } from '../../controller/hooks/useActiveReferenceBarID.ts';
import { useMlsStatusPinColor } from '../../modules/referenceView/hooks/useMlsStatusPinColor.ts';
import { ReferenceViewID, isIDSubject } from '../../modules/referenceView/index.ts';
import { selectReferenceBarUI } from '../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { Tkv2ReferenceBarItem } from './Tkv2ReferenceBarItem.tsx';
import { propertySectionTiles } from './consts.ts';
import { makeReferencePropertyItemID } from './utils/makeReferencePropertyItemID.ts';
import { useMemo } from 'react';
import { selectInspectifyReportId } from '../../redux/features/selectors/orderSelectors.ts';

export type Tkv2ReferenceBarPropertyProps = {
  property: Tkv2ReferenceBarPropertyType;
  expanded: boolean;
  onExpansionClick: (id: ReferenceViewID) => void;
};

export const Tkv2ReferenceBarProperty = ({
  property,
  expanded,
  onExpansionClick,
}: Tkv2ReferenceBarPropertyProps) => {
  const { referenceViewOpen, selectedDataSheet } = useAppSelector(selectReferenceBarUI);
  const activeID = useActiveReferenceBarID();
  const handleIconClick = useTkv2HandleIconClick(property.id);
  const isSubject = isIDSubject(property.id);
  const propertyIconLabel = isSubject ? 'Subject' : `Comp ${property.mapPinLabel}`;
  const pinColor = useMlsStatusPinColor(property.id);
  const isPairedSales = useLocation().pathname.includes('paired-sales');
  const reportId = useAppSelector(selectInspectifyReportId);

  if (!property.id) {
    return null;
  }

  const tiles = useMemo(() => {
    let tilesList = [...propertySectionTiles];

    if (reportId && isSubject) {
      tilesList.unshift({
        id: 'inspection-report',
        label: 'Inspection Report',
        icon: 'inspectifyReport',
        dataSheet: 'inspection-report',
      });
    }

    return tilesList;
  }, [propertySectionTiles, reportId]);

  return (
    <Flex flexDirection="column" width="100%" alignItems="center" justifyContent="center">
      <Tooltip label={propertyIconLabel} placement="left">
        <Flex
          position="relative"
          width="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <IconButton
            ariaLabel={propertyIconLabel}
            onClick={() => onExpansionClick(property.id)}
            variant="tertiary"
            size="md"
            icon={isSubject ? 'home-heart-filled' : 'map-pin-filled'}
            iconColor={isSubject ? 'statusOverdue' : pinColor}
            iconSize="lg"
            __dangerDoNotUseSx={{
              // fake in the icon below; this way, that
              // icon becomes "part of the button" visually
              height: expanded ? undefined : '4rem',
              justifyContent: 'center',
              alignItems: 'flex-start',
            }}
          />
          {!expanded && (
            <Flex marginTop="-1.75rem" __dangerDoNotUseSx={{ pointerEvents: 'none' }}>
              <Icon icon="arrow-down-s" size="lg" color="surfaceAccent4" />
            </Flex>
          )}
          {!isSubject && (
            <Flex position="absolute" top=".875rem">
              <Typography
                color="textOffset"
                variant="h5"
                __dangerDoNotUseSx={{
                  fontSize: '9px',
                  lineHeight: '0.75rem',
                  fontWeight: 600,
                  pointerEvents: 'none',
                }}
              >
                {property.mapPinLabel}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Tooltip>
      {expanded &&
        tiles.map((item) => {
          const isSelected =
            referenceViewOpen && activeID === property.id && item.dataSheet === selectedDataSheet;
          return (
            <Tkv2ReferenceBarItem
              id={makeReferencePropertyItemID(
                item.id,
                isSubject,
                isPairedSales,
                property.mapPinLabel,
              )}
              key={item.label}
              label={item.label}
              icon={item.icon}
              isSelected={isSelected}
              onClick={() => handleIconClick(item.dataSheet)}
            />
          );
        })}
    </Flex>
  );
};
